'use client';

import { getCurrentVariant, type ABVariant, type DeviceType } from './abTesting';
import { getReferral } from './referralTracking';
import { getPostHog } from './posthog';
import type { AnalyticsEventType } from '@boty/database';

/**
 * Analytics Tracking Module
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 * Story 6.5: Viral Tracking and Attribution
 * Story 6.6: A/B Variant Refinement & Admin Analytics (PostHog integration)
 *
 * Tracks:
 * - Page views with variant metadata
 * - CTA clicks
 * - Scroll depth (for Variant B)
 * - Time on page
 * - Viral events with referral attribution (Story 6.5)
 *
 * This module provides:
 * - PostHog event tracking for A/B analytics and session replay (Story 6.6)
 * - Client-side event queue for GA4 integration
 * - Server-side tracking via /api/analytics/track for viral metrics (K-factor)
 */

export interface AnalyticsEvent {
  event: string;
  variant?: ABVariant;
  device?: DeviceType;
  timestamp: number;
  properties?: Record<string, unknown>;
}

// Event queue for batching (future GA4 integration)
const eventQueue: AnalyticsEvent[] = [];

/**
 * Check if we're in development mode
 */
function isDev(): boolean {
  return process.env.NODE_ENV === 'development';
}

/**
 * Send event to PostHog (Story 6.6)
 *
 * Captures event with properties, silently fails if PostHog not initialized.
 */
function sendToPostHog(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  const posthog = getPostHog();
  if (posthog) {
    posthog.capture(eventName, properties);

    if (isDev()) {
      console.log('[PostHog]', eventName, properties);
    }
  }
}

/**
 * Send an analytics event
 *
 * In development: Logs to console
 * In production: Placeholder for GA4/analytics provider
 */
function sendEvent(event: AnalyticsEvent): void {
  eventQueue.push(event);

  if (isDev()) {
    console.log('[Analytics]', event.event, event);
  }

  // TODO: GA4 integration
  // gtag('event', event.event, {
  //   variant: event.variant,
  //   device: event.device,
  //   ...event.properties,
  // });
}

/**
 * Get current variant metadata for events
 */
function getVariantMetadata(): { variant?: ABVariant; device?: DeviceType } {
  const assignment = getCurrentVariant();
  if (assignment) {
    return {
      variant: assignment.variant,
      device: assignment.device,
    };
  }
  return {};
}

/**
 * Track page view with A/B variant metadata
 */
export function trackPageView(page: string): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'page_view',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      page,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    },
  });
}

/**
 * Track CTA click
 *
 * Story 6.6: Also sends to PostHog with CTA variant
 */
export function trackCTAClick(
  ctaName: string,
  ctaLocation?: string,
  ctaVariant?: string
): void {
  const metadata = getVariantMetadata();

  // PostHog tracking (Story 6.6)
  sendToPostHog('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    cta_variant: ctaVariant,
    page_variant: metadata.variant,
  });

  // Client-side event queue
  sendEvent({
    event: 'cta_click',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      cta_name: ctaName,
      cta_location: ctaLocation,
    },
  });
}

/**
 * Track scroll depth
 * Used primarily for Variant B (vertical scroll layout)
 */
export function trackScrollDepth(depth: number): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'scroll_depth',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      depth_percent: depth,
    },
  });
}

/**
 * Track time on page
 */
export function trackTimeOnPage(seconds: number): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'time_on_page',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      seconds,
    },
  });
}

/**
 * Track bot creation started
 */
export function trackBotCreationStarted(): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'bot_creation_started',
    ...metadata,
    timestamp: Date.now(),
  });
}

/**
 * Track bot creation completed
 */
export function trackBotCreationCompleted(): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'bot_creation_completed',
    ...metadata,
    timestamp: Date.now(),
  });
}

/**
 * Track content added (file, URL, text, etc.)
 */
export function trackContentAdded(contentType: string): void {
  const metadata = getVariantMetadata();

  sendEvent({
    event: 'content_added',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      content_type: contentType,
    },
  });
}

/**
 * Create a scroll depth tracker (factory function, NOT a React hook)
 *
 * Creates a scroll listener and returns a cleanup function.
 * Must be called inside useEffect for proper lifecycle management.
 *
 * @example
 * ```tsx
 * useEffect(() => {
 *   const cleanup = useScrollTracking();
 *   return cleanup;
 * }, []);
 * ```
 *
 * @returns Cleanup function to remove scroll listener, or undefined if SSR
 */
export function useScrollTracking(): (() => void) | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  let maxDepth = 0;
  const thresholds = [25, 50, 75, 90, 100];

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const currentDepth = Math.round((scrollTop / docHeight) * 100);

    // Track when crossing thresholds
    for (const threshold of thresholds) {
      if (currentDepth >= threshold && maxDepth < threshold) {
        trackScrollDepth(threshold);
      }
    }

    maxDepth = Math.max(maxDepth, currentDepth);
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
}

/**
 * Create a time-on-page tracker (factory function, NOT a React hook)
 *
 * Tracks time milestones at intervals: 30s, 60s, 120s, 300s.
 * Must be called inside useEffect for proper lifecycle management.
 *
 * @example
 * ```tsx
 * useEffect(() => {
 *   const cleanup = useTimeTracking();
 *   return cleanup;
 * }, []);
 * ```
 *
 * @returns Cleanup function to clear the timer, or undefined if SSR
 */
export function useTimeTracking(): (() => void) | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const startTime = Date.now();
  const intervals = [30, 60, 120, 300];
  const trackedIntervals = new Set<number>();

  const checkTime = () => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);

    for (const interval of intervals) {
      if (elapsed >= interval && !trackedIntervals.has(interval)) {
        trackTimeOnPage(interval);
        trackedIntervals.add(interval);
      }
    }
  };

  const timer = setInterval(checkTime, 5000);

  // Return cleanup function
  return () => {
    clearInterval(timer);
  };
}

/**
 * Get all tracked events (for debugging)
 */
export function getEventQueue(): AnalyticsEvent[] {
  return [...eventQueue];
}

/**
 * Clear the event queue (for testing)
 */
export function clearEventQueue(): void {
  eventQueue.length = 0;
}

// ============================================================================
// VIRAL TRACKING (Story 6.5)
// ============================================================================

/**
 * Send a viral tracking event to the server
 * Used for K-factor calculation and referral attribution
 */
async function sendViralEvent(
  eventType: AnalyticsEventType,
  options: {
    sessionId?: string | undefined;
    userId?: string | undefined;
    botId?: string | undefined;
    referrerBotId?: string | undefined;
    properties?: Record<string, unknown> | undefined;
  } = {}
): Promise<void> {
  // Only send in browser
  if (typeof window === 'undefined') return;

  // Get referrer from storage if not provided
  const referrerBotId = options.referrerBotId || getReferral() || undefined;

  try {
    await fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType,
        sessionId: options.sessionId,
        userId: options.userId,
        botId: options.botId,
        referrerBotId,
        properties: options.properties,
      }),
    });

    if (isDev()) {
      console.log('[Analytics] Viral event sent:', eventType, {
        referrerBotId,
        ...options,
      });
    }
  } catch (error) {
    // Silent fail - don't break user experience for analytics
    if (isDev()) {
      console.error('[Analytics] Failed to send viral event:', error);
    }
  }
}

/**
 * Track landing page visit with referral attribution
 * Call this when the landing page loads with a referral parameter
 *
 * Story 6.6: Also sends to PostHog with all variant metadata
 *
 * @param referrerBotId - Optional: Bot ID from ?ref= parameter
 * @param variants - Optional: All variant assignments for PostHog
 */
export function trackLandingPageVisit(
  referrerBotId?: string,
  variants?: {
    pageVariant?: string;
    headlineVariant?: string;
    ctaVariant?: string;
    subheadVariant?: string;
  }
): void {
  const metadata = getVariantMetadata();

  // PostHog tracking (Story 6.6)
  sendToPostHog('landing_page_view', {
    page_variant: variants?.pageVariant || metadata.variant,
    headline_variant: variants?.headlineVariant,
    cta_variant: variants?.ctaVariant,
    subhead_variant: variants?.subheadVariant,
    referrer_bot_id: referrerBotId,
    $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  // Client-side event queue
  sendEvent({
    event: 'landing_page_visit',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      referrer_bot_id: referrerBotId,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    },
  });

  // Server-side viral tracking (Supabase for K-factor)
  sendViralEvent('landing_page_visit', { referrerBotId });
}

/**
 * Track bot creation started with referral attribution
 * Call this when user begins creating a bot
 *
 * Story 6.6: Also sends to PostHog
 *
 * @param referrerBotId - Optional: Referring bot ID
 */
export function trackViralBotCreationStarted(referrerBotId?: string): void {
  const metadata = getVariantMetadata();

  // PostHog tracking (Story 6.6)
  sendToPostHog('bot_creation_started', {
    referrer_bot_id: referrerBotId,
    page_variant: metadata.variant,
  });

  // Client-side event queue
  sendEvent({
    event: 'bot_creation_started',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      referrer_bot_id: referrerBotId,
    },
  });

  // Server-side viral tracking (Supabase for K-factor)
  sendViralEvent('bot_creation_started', { referrerBotId });
}

/**
 * Track bot creation completed with referral attribution
 * Call this when a bot is successfully created
 *
 * Story 6.6: Also sends to PostHog
 *
 * @param botId - The created bot's ID
 * @param referrerBotId - Optional: Referring bot ID
 */
export function trackViralBotCreationCompleted(
  botId: string,
  referrerBotId?: string,
  viralCTAVariant?: string
): void {
  const metadata = getVariantMetadata();

  // PostHog tracking (Story 6.6 + Story 6.11)
  sendToPostHog('bot_creation_completed', {
    bot_id: botId,
    referrer_bot_id: referrerBotId,
    viral_cta_variant: viralCTAVariant,
    page_variant: metadata.variant,
  });

  // Client-side event queue
  sendEvent({
    event: 'bot_creation_completed',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      bot_id: botId,
      referrer_bot_id: referrerBotId,
      viral_cta_variant: viralCTAVariant,
    },
  });

  // Server-side viral tracking (Supabase for K-factor)
  sendViralEvent('bot_creation_completed', {
    botId,
    referrerBotId,
    properties: { viral_cta_variant: viralCTAVariant },
  });
}

/**
 * Track signup completed with referral attribution
 * Call this when a user completes signup/registration
 *
 * Story 6.6: Also sends to PostHog and links user identity
 *
 * @param userId - The new user's ID
 * @param email - Optional: User's email for easier PostHog session filtering
 * @param referrerBotId - Optional: Referring bot ID
 */
export function trackSignupCompleted(
  userId: string,
  email?: string,
  referrerBotId?: string
): void {
  const metadata = getVariantMetadata();

  // PostHog tracking (Story 6.6)
  // Identify the user in PostHog with email for easier session targeting
  const posthog = getPostHog();
  if (posthog) {
    posthog.identify(userId, email ? { email } : undefined);
  }
  sendToPostHog('signup_completed', {
    user_id: userId,
    referrer_bot_id: referrerBotId,
    page_variant: metadata.variant,
  });

  // Client-side event queue
  sendEvent({
    event: 'signup_completed',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      user_id: userId,
      referrer_bot_id: referrerBotId,
    },
  });

  // Server-side viral tracking (Supabase for K-factor)
  sendViralEvent('signup_completed', { userId, referrerBotId });
}

/**
 * Identify an authenticated user with PostHog
 * Call this when a user logs in or on page load if already authenticated
 *
 * Story 6.6: Links PostHog session to user identity for easier targeting
 *
 * @param userId - The user's ID
 * @param email - The user's email for easier PostHog session filtering
 */
export function identifyAuthenticatedUser(
  userId: string,
  email: string
): void {
  const posthog = getPostHog();
  if (posthog) {
    posthog.identify(userId, { email });
  }
}

// ============================================================================
// LANDING PAGE TRACKING (Story 6.14)
// ============================================================================

/**
 * Track landing page viewed (impression)
 * Call once per session when a landing page mounts
 *
 * Story 6.14: Landing Pages for Services & Personal Brands
 *
 * @param pageType - The landing page type: 'service' | 'personal-brand'
 * @param locale - Current locale
 * @param referrer - Document referrer
 */
export function trackLandingPageViewed(
  pageType: 'service' | 'personal-brand',
  locale: string,
  referrer: string
): void {
  sendToPostHog('landing_page_viewed', {
    page_type: pageType,
    locale,
    referrer,
  });

  sendEvent({
    event: 'landing_page_viewed',
    timestamp: Date.now(),
    properties: {
      page_type: pageType,
      locale,
      referrer,
    },
  });
}

/**
 * Track landing page CTA clicked
 * Call when user clicks the CTA button on a landing page
 *
 * Story 6.14: Landing Pages for Services & Personal Brands
 *
 * @param pageType - The landing page type: 'service' | 'personal-brand'
 * @param locale - Current locale
 * @param referrer - Document referrer
 */
export function trackLandingCtaClicked(
  pageType: 'service' | 'personal-brand',
  locale: string,
  referrer: string
): void {
  sendToPostHog('landing_cta_clicked', {
    page_type: pageType,
    locale,
    referrer,
  });

  sendEvent({
    event: 'landing_cta_clicked',
    timestamp: Date.now(),
    properties: {
      page_type: pageType,
      locale,
      referrer,
    },
  });
}

// ============================================================================
// INSPIRATION FEED TRACKING (Story 6.13)
// ============================================================================

/**
 * Track inspiration feed viewed (impression)
 * Call once per session when feed becomes visible
 *
 * Story 6.13: Bot Inspiration Feed on Landing Page
 */
export function trackInspirationFeedViewed(
  pageVariant: string,
  feedPosition: string,
  ideaCount: number
): void {
  const metadata = getVariantMetadata();

  sendToPostHog('inspiration_feed_viewed', {
    page_variant: pageVariant,
    feed_position: feedPosition,
    idea_count: ideaCount,
  });

  sendEvent({
    event: 'inspiration_feed_viewed',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      page_variant: pageVariant,
      feed_position: feedPosition,
      idea_count: ideaCount,
    },
  });
}

/**
 * Track inspiration idea clicked
 * Call when user clicks a bot idea chip
 *
 * Story 6.13: Bot Inspiration Feed on Landing Page
 */
export function trackInspirationIdeaClicked(
  ideaName: string,
  category: string,
  pageVariant: string,
  feedPosition: string
): void {
  const metadata = getVariantMetadata();

  sendToPostHog('inspiration_idea_clicked', {
    idea_name: ideaName,
    category,
    page_variant: pageVariant,
    feed_position: feedPosition,
  });

  sendEvent({
    event: 'inspiration_idea_clicked',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      idea_name: ideaName,
      category,
      page_variant: pageVariant,
      feed_position: feedPosition,
    },
  });
}

/**
 * Track bot deployed (first public share)
 * Call this when a bot is first published/deployed
 *
 * @param botId - The deployed bot's ID
 */
export function trackBotDeployed(botId: string): void {
  const metadata = getVariantMetadata();

  // Client-side event
  sendEvent({
    event: 'bot_deployed',
    ...metadata,
    timestamp: Date.now(),
    properties: {
      bot_id: botId,
    },
  });

  // Server-side viral tracking
  sendViralEvent('bot_deployed', { botId });
}
